import { useState } from 'react';
import { AxiosError } from 'axios';
import { DsAlert, DsButton, DsInput, DsTextarea } from '@/modules/internal-v2/ds';
import {
  useMaintenance,
  useUpdateMaintenance,
} from '@/api/announcements/announcements.hooks';
import { Maintenance } from '@/api/announcements/announcements.types';

function errorMessage(error: AxiosError): string {
  return (
    (error.response?.data as { message?: string })?.message ??
    'Speichern fehlgeschlagen.'
  );
}

/** Maintenance-mode toggle + message editor. Reads/writes `/v1/maintenance`. */
export function MaintenanceCard() {
  const { data, isLoading, isError } = useMaintenance();

  if (isLoading) {
    return (
      <div className="ann-card">
        <div className="ann-empty">Lädt…</div>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="ann-card">
        <div className="ann-empty">
          Wartungseinstellungen konnten nicht geladen werden.
        </div>
      </div>
    );
  }
  // Mount the form only once data is loaded, so it can seed its state from
  // props in useState initializers (no setState-in-effect). Keyed by the saved
  // values so a refetch after save re-seeds the fields to the persisted state.
  return (
    <MaintenanceForm
      key={`${data.enabled}|${data.title}|${data.message}`}
      initial={data}
    />
  );
}

function MaintenanceForm({ initial }: { initial: Maintenance }) {
  const updateMaintenance = useUpdateMaintenance();
  const [enabled, setEnabled] = useState(initial.enabled);
  const [title, setTitle] = useState(initial.title);
  const [message, setMessage] = useState(initial.message);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    updateMaintenance.mutate(
      { enabled, title: title.trim(), message: message.trim() },
      { onError: (e) => setError(errorMessage(e)) },
    );
  };

  return (
    <div className="ann-card">
      <div className="ann-card__head">
        <span className="ann-card__title">Wartungsmodus</span>
        <label className="ann-check">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Aktiv
        </label>
      </div>

      {error && <div className="ds-alert error">{error}</div>}

      <div className="ann-form">
        <label className="ann-field">
          <span className="ann-field-label">Titel</span>
          <DsInput
            value={title}
            placeholder="Wartungsarbeiten"
            onChange={(e) => setTitle(e.target.value)}
          />
        </label>
        <label className="ann-field">
          <span className="ann-field-label">Nachricht</span>
          <DsTextarea
            value={message}
            rows={3}
            placeholder="Wir sind in Kürze wieder für Sie da."
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>

        {enabled && (
          <DsAlert severity="warn" className="ann-maint-warn">
            Bei aktivem Wartungsmodus sind externe Kunden ausgesperrt. Interne
            Benutzer behalten vollen Zugriff.
          </DsAlert>
        )}

        <div>
          <DsButton
            variant="primary"
            disabled={updateMaintenance.isPending}
            onClick={handleSave}
          >
            {updateMaintenance.isPending ? 'Speichert…' : 'Speichern'}
          </DsButton>
        </div>
      </div>
    </div>
  );
}
